import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import PlayView from './play/PlayView';
import PlayByToken from './play/PlayByToken';
import { STATIC_ROUND } from './data/staticRound';
import LoginScreen from './auth/LoginScreen';
import GoogleCallback from './auth/GoogleCallback';
import AppShell from './app/AppShell';
import RoundsPage from './rounds/RoundsPage';
import RoundEditor, { EndlessEditor, FakeEditor, IntakeEditor } from './rounds/RoundEditor';
import PreviewRound from './play/PreviewRound';
import ModesHome from './modes/ModesHome';
import ModesSeriesList from './modes/ModesSeriesList';
import ModesPlaySeries from './modes/ModesPlaySeries';
import ModesEndless from './modes/ModesEndless';
import ConnectExtension from './app/ConnectExtension';
import { useAuth } from './lib/useAuth';
import { rememberReturnPath } from './lib/googleAuth';
import './styles/app.css';

/* Two zones, and the split is the whole point:

   PUBLIC — anything a camera might be pointed at. No login, no chrome, no redirect chain.
   A share link that asks Adorian to sign in seconds before a take is a shoot-day outage, and
   the payload is only headlines and graphics.

   SIGNED IN — the builder. Roster-gated to the Decked team. */

function PublicRoutes() {
  return (
    <Routes>
      <Route path="/play/:token" element={<PlayByToken />} />
      {/* The original ten-headline round, kept as a login-free reference and smoke target. */}
      <Route path="/demo" element={<PlayView round={STATIC_ROUND} />} />
    </Routes>
  );
}

const PUBLIC_PREFIXES = ['/play/', '/demo'];
const isPublicPath = (pathname: string) => PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));

export default function App() {
  const location = useLocation();
  const auth = useAuth();

  // Remember where they were headed, so Google returns them there and not to the root.
  useEffect(() => {
    if (auth.status === 'anonymous' && !isPublicPath(location.pathname)) {
      rememberReturnPath(location.pathname + location.search);
    }
  }, [auth.status, location.pathname, location.search]);

  // The play surface and the OAuth callback must resolve without waiting on the roster check.
  if (isPublicPath(location.pathname)) return <PublicRoutes />;
  if (location.pathname.startsWith('/auth/google/callback')) {
    return (
      <Routes>
        <Route path="/auth/google/callback" element={<GoogleCallback />} />
      </Routes>
    );
  }

  if (auth.status === 'checking') {
    return (
      <div className="state-block">
        <div className="big">Checking your access</div>
        <div className="sub">One moment…</div>
      </div>
    );
  }

  if (auth.status !== 'authenticated' || !auth.member) {
    return (
      <LoginScreen
        notice={auth.status === 'refused' ? auth.reason : null}
        noticeIsTransient={auth.transient}
        onRetry={auth.recheck}
      />
    );
  }

  /* Preview is signed-in but renders WITHOUT the app chrome — it has to look exactly like the
     recording surface, or it is not a preview. */
  if (/^\/rounds\/[^/]+\/preview/.test(location.pathname)) {
    return (
      <Routes>
        <Route path="/rounds/:roundId/preview" element={<PreviewRound />} />
        <Route path="/rounds/:roundId/preview/:slug" element={<PreviewRound />} />
      </Routes>
    );
  }

  /* Adorian's side. Signed in — he is on the Decked roster like everyone else, and this app has
     no role tiering — but rendered outside AppShell for the same reason preview is: a header
     and a Sign Out button must never be able to appear in a recording. */
  if (location.pathname.startsWith('/modes')) {
    return (
      <Routes>
        <Route path="/modes" element={<ModesHome />} />
        <Route path="/modes/series" element={<ModesSeriesList />} />
        <Route path="/modes/series/:roundId" element={<ModesPlaySeries />} />
        <Route path="/modes/endless" element={<ModesEndless />} />
        <Route path="/modes/*" element={<Navigate to="/modes" replace />} />
      </Routes>
    );
  }

  return (
    <AppShell member={auth.member} onSignOut={auth.signOut}>
      <Routes>
        <Route path="/" element={<RoundsPage />} />
        <Route path="/rounds/:roundId" element={<RoundEditor />} />
        {/* Both reserved pools are singletons, so they are addressed by name rather than by id. */}
        <Route path="/endless" element={<EndlessEditor />} />
        <Route path="/fake" element={<FakeEditor />} />
        {/* Inside the signed-in zone deliberately: the roster gate is the authorisation for
            handing a session to the extension, and this page must not be the thing deciding. */}
        <Route path="/connect" element={<ConnectExtension />} />
        <Route path="/intake" element={<IntakeEditor />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}
